import styled, { css } from 'styled-components';
import { Sizes } from 'styles/constants';
import { Colors } from 'styles/enums';
import { mediaQueries } from 'styles/helpers';
import type { ContentTypes } from './types';

export const ContentContainer = styled.div<ContentTypes>`
  position: relative;
  ${({ $variant }) => $variant !== 'video' && `max-width: ${Sizes.maxWidth}px;`}
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 36px 0px;
  padding: 20px;
  background-color: ${({ $variant }) => generateBackgroundColor($variant)};
  z-index: 8;

  > div[data-cy='Grid'] {
    width: 100%;
    min-height: 0px;
  }

  ${({ $variant }) => generateMediaQueries($variant)}
`;

export const ImageWrapper = styled.div<ContentTypes>`
  position: relative;
  height: 100%;
  display: flex;
  justify-content: center;

  ${({ $variant }) => $variant !== 'logo' && 'min-height: 260px;'}

  ${({ $variant, $layout }) =>
    $variant === 'logo' &&
    css`
    > div {
      min-width: 160px;
      min-height: ${$layout === 'logo_only' ? '350px' : '190px'};
      max-width: ${$layout === 'logo_only' ? '100%' : '160px'};
      max-height: ${$layout === 'logo_only' ? '600px' : '190px'};

      ${mediaQueries(
        'md',
        css`
          min-width: 240px;
          min-height: 300px;
          max-width: ${$layout === 'logo_only' ? '50%' : '245px'};
          max-height: ${$layout === 'logo_only' ? '500px' : '310px'};
        `
      )}
    }
  `}
`;

export const CenterBox = styled.div<ContentTypes>`
  ${({ $variant }) => $variant !== 'logo' && $variant !== 'video' && 'height: 100%;'}
  display: ${({ $hasLink }) => ($hasLink ? 'grid' : 'flex')};
  ${({ $hasLink }) => !$hasLink && 'flex-direction: column;'}
  padding: ${({ $variant }) => ($variant === 'logo' ? '0px' : '20px')};
  background-color: ${({ $variant }) => ($variant === 'logo' ? Colors.UI.transparent : Colors.UI.white)};

  a {
    align-self: flex-start;
    justify-self: end;

    p {
      margin-bottom: 0;
    }
  }

  ${({ $variant, $noMargin }) => $variant === 'video' && generateVideoStyles($noMargin)}
  ${() => generateFontStyles()}

  > p, > small {
    ${({ $variant }) => $variant !== 'video' && 'display: none;'}
  }

  ${mediaQueries(
    'md',
    css`
      > p, > small {
        display: inline-block;
        margin-bottom: 24px;
      }
    `
  )}

  ${({ $noMargin }) =>
    mediaQueries(
      'lg',
      css`
      padding: 32px 48px;
      padding: ${$noMargin ? '32px 0px' : '32px 48px'};
      gap: 0px;

      small {
        margin-bottom: 32px;
      }
    
      h5 {
        margin-bottom: 16px;
      }
    
      a {
        align-self: flex-end;
      }
    `
    )}

  ${({ $variant }) =>
    $variant === 'logo' &&
    mediaQueries(
      'lg',
      css`
      padding: 0px;
    `
    )}
`;

export const VideoWrapper = styled.div`
  position: relative;
  height: 100%;
  min-height: 270px;
  min-width: 350px;

  svg {
    position: absolute;
    top: calc(50% - 66px);
    left: calc(50% - 66px);
  }

  ${mediaQueries(
    'md',
    css`
      min-height: 470px;
      min-width: 740px;
    `
  )}
`;

const generateBackgroundColor = (variant?: string) => {
  if (!variant) return Colors.Turkish.base100;

  switch (variant) {
    case 'video':
      return Colors.Yellow.base100;
    case 'logo':
      return Colors.UI.beigeWhite;
    default:
      return Colors.Turkish.base100;
  }
};

const generateMediaQueries = (variant?: string) => {
  if (!variant) return '';
  return mediaQueries(
    'lg',
    css`
      padding: ${variant !== 'logo' ? '80px 0px' : '0px'};
      background-color: ${variant === 'logo' ? Colors.UI.beigeWhite : Colors.Turkish.base100};

      > div[data-cy='Grid'] {
        width: 85%;
        min-height: 320px;
      }
    `
  );
};

const generateVideoStyles = (noMargin?: boolean) => {
  return css`
    margin: ${noMargin ? '0px' : '-22px 22px 0px 22px'};
    ${!noMargin && 'min-height: 166px;'}
    z-index: 1;

    ${mediaQueries(
      'md',
      css`
        margin: ${noMargin ? '0px' : '0px 0px 0px -120px'};
        ${!noMargin && 'min-height: 316px;'}
        z-index: 2;
      `
    )}
  `;
};

const generateFontStyles = () => {
  return `
    small {
      font-weight: 300;
    }

    a > span {
      font-size: 18px;
      line-height: 28px;
      max-width: 100%;
    }
  `;
};
